/**
 * The words `client/requests.html` prints for a closed set of codes, and the one
 * derivation `DESIGN.md` licenses.
 *
 * Nothing here is a fact. `STATUS_WORD` and `CONTENT_TYPE_PLURAL` are label maps
 * over enums `packages/shared` owns — the same shape `DecisionCard`'s own
 * `DECISION_TAG` is, and for the same reason: the *word* a state prints as is the
 * UI's, the state itself is the API's. A page that spelt `in_progress` would be
 * printing a column name at a client.
 *
 * `creditsByType` is the exception and it is deliberate. `DESIGN.md` §Non-negotiable:
 * "a **subtotal of rows that are visible on the same page** (including inside the
 * `<details>` that the subtotal summarises) is a UI computation, not an invented
 * fact… It must reconcile exactly, and its components must remain reachable." The
 * content plan's four columns — *Reels 8 / 40 credits* — are exactly that: a group-by
 * over `ContentPlanView.items`, whose 18 rows the card lists in a fold beside them,
 * and whose total is `credits_planned` (62) from the same payload. There is no
 * per-type field on `ContentPlanSchema` to read instead.
 */

import type {
  ClientDecisionItemView,
  ContentPlanItemView,
  ContentTypeCode,
  DecisionKind,
  WorkOrderState,
  WorkOrderTiming,
} from '@fareast/shared'
import { CONTENT_TYPE_CODES, CONTENT_TYPE_NOUNS, sentenceCase } from '@fareast/shared'

import { DECISION_TAG } from '../../../components/client/index.js'
import type { StepState } from '../../../components/portal/Stepper.js'

/**
 * What a status reads as in the ledger's *Status* column.
 *
 * The mockup's words were the state names with the underscore spent — "draft",
 * "proposed" — which are the studio's words for where a work order is. Session 54 says
 * them as the client's: a `draft` is a request the studio has not priced yet, and a
 * `proposed` one is a price waiting on the client.
 */
export const STATUS_WORD: Readonly<Record<WorkOrderState, string>> = {
  draft: 'Being priced',
  proposed: 'Awaiting approval',
  approved: 'Approved',
  in_progress: 'In progress',
  delivered: 'Delivered',
  billed: 'Billed',
  declined: 'Declined',
  cancelled: 'Cancelled',
}

/**
 * The happy path, in order — the six `.step`s in the mockup's own "how this works"
 * stepper. `declined` and `cancelled` are terminal exits from it rather than places
 * along it, which is why they are not drawn.
 */
export const LIFECYCLE: readonly WorkOrderState[] = [
  'draft',
  'proposed',
  'approved',
  'in_progress',
  'delivered',
  'billed',
]

/**
 * The one qualifying sentence under the hero: "Three of them carry money. The
 * fourth is a sign-off with none — and it is the only one with a deadline."
 *
 * Both numbers are `ClientDecisionCountsSchema`'s, which are read off `items`
 * server-side so the hero and the cards cannot disagree. The three money branches
 * exist because "the rest" is a lie when there is no rest.
 *
 * **The deadline half of the mockup's sentence is `late`**, and it is the only
 * deadline field this page has. `OpenDecisionSchema.valid_until` is set on a quote
 * and an extra and is **null** for the two the mockup calls out
 * (`retainers.openDecisionsForClient` writes it that way), so a sentence keyed on
 * `valid_until` would say the opposite of the mockup. `late` is what
 * `readmodels/client-decisions.ts` derives for exactly this — "a content plan past
 * its first item date" — and it is what its own sort orders on first. A clause
 * rather than a rewrite of the sentence: nothing being late is the ordinary case,
 * and P3 says show the exception.
 */
export function waitingSentence(
  counts: { readonly waiting: number; readonly with_money: number },
  items: readonly Pick<ClientDecisionItemView, 'late'>[],
  number: (value: number) => string,
): string {
  return `${moneyClause(counts, number)}${lateClause(items, number)}`
}

function moneyClause(
  counts: { readonly waiting: number; readonly with_money: number },
  number: (value: number) => string,
): string {
  if (counts.waiting === 0) return 'Nothing is waiting on you.'
  if (counts.with_money === 0) return 'None of them commits money.'
  if (counts.with_money === counts.waiting) return 'Every one of them commits money.'
  return `${number(counts.with_money)} of them commit money; the rest commit none.`
}

function lateClause(
  items: readonly Pick<ClientDecisionItemView, 'late'>[],
  number: (value: number) => string,
): string {
  const late = items.filter((item) => item.late).length
  if (late === 0) return ''
  if (late === 1) return ' One of them is already past its own date.'
  return ` ${number(late)} of them are already past their own dates.`
}

/**
 * The states the mockup folds away: "Three more, delivered or billed."
 *
 * Written as the set that is **finished** rather than as the set that is open,
 * because `workOrderMachine` grows at the open end — a status added tomorrow is
 * something waiting on somebody, and defaulting it into the visible list is the
 * safe direction.
 */
const SETTLED: readonly WorkOrderState[] = ['delivered', 'billed', 'declined', 'cancelled']

export function isSettled(status: WorkOrderState): boolean {
  return SETTLED.includes(status)
}

/**
 * Whether the state word in a ledger row is *where this request is now*.
 *
 * `Step state="now"` is ink, weight 600 **and** `aria-current="step"`, which is a
 * claim about one element: a table that passed it on every row told a screen reader
 * that twenty-five of them were the current step, and drew a **cancelled** work
 * order as the stage the reader is at. A row that has stopped moving keeps the word
 * and loses the emphasis.
 */
export function stepStateFor(status: WorkOrderState): StepState | undefined {
  return isSettled(status) ? undefined : 'now'
}

/**
 * The chip in the "decided recently" fold — `client/requests.html` §1.
 *
 * One entry different from `DecisionCard`'s own `DECISION_TAG`, and spread from it
 * rather than retyped so the difference is the whole of the declaration. An open
 * `RET-CHG-` is drawn on a card headed "Retainer", because the card is about the
 * package it changes; a decided one is a line in a list of decisions, and the
 * mockup's fold calls it what it was — "Package change".
 */
export const DECIDED_TAG: Readonly<Record<DecisionKind, string>> = {
  ...DECISION_TAG,
  package_change: 'Package change',
}

/**
 * The composer's *Timing* segment, and how a request's own answer reads back.
 *
 * `WorkOrderTimingSchema`: "Rush is not a surcharge — it changes what the studio can
 * promise, not what it costs", which is why this is never beside a price.
 */
export const TIMING_WORD: Readonly<Record<WorkOrderTiming, string>> = {
  no_rush: 'No rush',
  this_month: 'This month',
  this_week: 'This week',
}

/** The three, in the order the mockup's segment prints them. */
export const TIMINGS: readonly WorkOrderTiming[] = ['no_rush', 'this_month', 'this_week']

/**
 * The line under the composer's urgency choice — the chosen one's, so the three words
 * on the buttons do not have to carry what each commits the studio to. None of them
 * mentions money, for `TIMING_WORD`'s reason.
 */
export const TIMING_HINT: Readonly<Record<WorkOrderTiming, string>> = {
  no_rush: 'Whenever there is room in the schedule.',
  this_month: 'Some time this month.',
  this_week: 'Urgent.',
}

/** `client/requests.html` §2's plan card: *Reels · Carousels · Statics · Stories*. */
/**
 * `Reels`, `Carousels`, `Statics`, `Stories` — a column header per content type.
 *
 * Derived rather than typed. `CONTENT_TYPE_NOUNS` is the property of the enum
 * (`packages/shared/src/pricing.ts`) and `sentenceCase` is the casing rule; four
 * files spelt these four words out before either existed, and one of them made
 * `story` into "storys".
 */
export const CONTENT_TYPE_PLURAL: Readonly<Record<ContentTypeCode, string>> = Object.fromEntries(
  CONTENT_TYPE_CODES.map((code) => [code, sentenceCase(CONTENT_TYPE_NOUNS[code].other)]),
) as Readonly<Record<ContentTypeCode, string>>

/**
 * Service code → the name the rate card gives it.
 *
 * A ledger row carries `service` as a code (`social_content_base`), and a code is a
 * column name. The client's own catalog is the only thing that knows what it is
 * called, and it already answers both halves — the monthly blocks and the project
 * services — so this is a lookup rather than a table this page keeps.
 */
export function serviceNamesOf(
  catalog:
    | {
        readonly monthly: readonly { readonly code: string; readonly name: string }[]
        readonly projects: readonly { readonly code: string; readonly name: string }[]
      }
    | undefined,
): ReadonlyMap<string, string> {
  const names = new Map<string, string>()
  if (catalog === undefined) return names
  for (const service of [...catalog.monthly, ...catalog.projects])
    names.set(service.code, service.name)
  return names
}

export type ContentTypeTally = {
  readonly code: ContentTypeCode
  readonly pieces: number
  readonly credits: number
}

/**
 * The plan's pieces, grouped by kind, in `CONTENT_TYPE_CODES`' own order.
 *
 * A kind with no pieces in the plan is left out rather than printed as a zero —
 * `DESIGN.md` P3, show the exception. The tallies sum to `credits_planned`, which
 * the meter beside them prints, so the two cannot disagree.
 */
export function creditsByType(items: readonly ContentPlanItemView[]): readonly ContentTypeTally[] {
  return CONTENT_TYPE_CODES.map((code) => {
    const mine = items.filter((item) => item.content_type === code)
    return {
      code,
      pieces: mine.length,
      credits: mine.reduce((total, item) => total + item.credits, 0),
    }
  }).filter((tally) => tally.pieces > 0)
}
